import { useEffect } from 'react';

import { useApplicationContext } from 'app/store/сontext/ApplicationContext';
import googleTagManager from 'app/analytics/GoogleTagManager';
import { AnalyticsActionTypeName } from 'app/analytics/AnalyticsActionTypeName';

const DISPLAYED_EVENT_SUFFIX = 'Displayed';
const CONFIRM_ACTION_INITIATED_EVENT_SUFFIX = 'ConfirmActionInitiated';
const BACK_ACTION_INITIATED_EVENT_SUFFIX = 'BackActionInitiated';
const RESOLVED_ACTION_EVENT_SUFFIX = 'ActionResolved';
const REJECTED_EVENT_SUFFIX = 'ActionRejected';

interface IAnalyticsAttributes {
    dop_info: string;
    utm_source: string;
    internal_id?: string;
    cuid?: string;
}

export interface ReturnAnalyticsType {
    pageDisplayed: () => void;

    confirm: (options?: { productCode: string }) => void;

    resolve: (actionName: AnalyticsActionTypeName) => void;

    reject: () => void;

    back: () => void;

    common: (commonSuffix: string) => void;

    fullEvent: (fullEventName: string) => void;
}

export const useAnalytics = (
    productIdentifier: string,
    pagePrefix: string,
    isInitialize = true,
): ReturnAnalyticsType => {
    const { channel, utmSource, internalId, logger, cuid } =
        useApplicationContext();

    const attributes: IAnalyticsAttributes = {
        cuid,
        dop_info: channel,
        utm_source: utmSource,
        internal_id: internalId,
    };

    const sendEvent = (eventParts: string[]) => {
        googleTagManager.sendEvent(eventParts, attributes);
        logger.info([...eventParts, JSON.stringify(attributes)].join(' '));
    };

    function common(commonPrefix: string, productCode?: string) {
        const eventParts = [productCode || productIdentifier, pagePrefix + commonPrefix];
        sendEvent(eventParts);
    }

    function fullEvent(fullEventName: string) {
        const eventParts = [productIdentifier, fullEventName];
        sendEvent(eventParts);
    }

    function resolve(actionName: AnalyticsActionTypeName) {
        const eventParts = [
            productIdentifier,
            pagePrefix + actionName + RESOLVED_ACTION_EVENT_SUFFIX,
        ];

        sendEvent(eventParts);
    }

    useEffect(() => {
        if (isInitialize) {
            common(DISPLAYED_EVENT_SUFFIX);
        }
    }, []);

    function pageDisplayed() {
        common(DISPLAYED_EVENT_SUFFIX);
    }

    function confirm(options?: { productCode: string }) {
        common(CONFIRM_ACTION_INITIATED_EVENT_SUFFIX, options?.productCode);
    }

    function reject() {
        common(REJECTED_EVENT_SUFFIX);
    }

    function back() {
        common(BACK_ACTION_INITIATED_EVENT_SUFFIX);
    }

    return { pageDisplayed, confirm, resolve, reject, back, common, fullEvent };
};
